<template>
  <CurrentEventDataProvider>
    <OpenSessionsDataProvider
      v-slot="{
        sessions,
        registeredCount,
        loading,
        failed,
        isPending,
        refresh,
        register,
        unregister
      }"
    >
      <ParticipantDashboardLayout>
        <div class="p-6 md:p-8 space-y-6 w-full">
          <!-- Header -->
          <header class="flex flex-col gap-6">
            <div class="flex items-start gap-3 min-w-0">
              <button
                type="button"
                class="shrink-0 inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 text-sm sm:text-base rounded-xl border border-slate-200 text-slate-600 hover:text-teal-600 hover:border-teal-300 transition-colors"
                aria-label="Back to dashboard"
                title="Back to dashboard"
                @click="$router.push({ name: 'dashboard' }).catch(() => {})"
              >
                <i class="fal fa-arrow-left"></i>
              </button>
              <div class="min-w-0">
                <h1
                  class="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight"
                >
                  {{ $t('Views.OpenSessions.title') }}
                </h1>
                <p class="text-sm text-slate-500 mt-1">
                  {{ $t('Views.OpenSessions.subtitle') }}
                </p>
              </div>
            </div>
            <NetworkingQuickNav />
          </header>

          <!-- Tabs -->
          <nav class="flex flex-wrap gap-6 sm:gap-8 border-b border-slate-200">
            <button
              v-for="tab in tabs"
              :key="tab.key"
              type="button"
              :class="[
                'pb-4 px-1 text-sm font-bold relative transition-all duration-200 outline-none flex items-center gap-2 whitespace-nowrap min-h-[44px]',
                activeTab === tab.key
                  ? 'text-teal-600 font-extrabold border-b-2 border-teal-600 -mb-px'
                  : 'text-slate-400 hover:text-slate-700'
              ]"
              @click="activeTab = tab.key"
            >
              <span>{{ $t('Views.OpenSessions.tab' + tab.label) }}</span>
              <span
                v-if="tab.key === 'mine' && registeredCount > 0"
                class="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-teal-50 text-teal-600 border border-teal-200 text-[10px] font-black"
              >
                {{ registeredCount }}
              </span>
            </button>
          </nav>

          <!-- Filters: only on All tab — sits above the day tabs -->
          <div
            v-if="activeTab === 'all'"
            class="flex flex-col sm:flex-row sm:items-center gap-3"
          >
            <!-- Search pill (shared component) -->
            <SearchPill
              v-model="filters.search"
              :placeholder="$t('Views.OpenSessions.searchPlaceholder')"
              class="w-full sm:flex-1 sm:min-w-0"
            />

            <!-- Sponsor dropdown -->
            <select
              v-model="filters.sponsorId"
              class="w-full sm:w-[15rem] sm:shrink-0 pl-5 bg-slate-50 text-slate-700 text-sm font-semibold rounded-full border border-slate-200 focus:border-teal-500 outline-none transition-all cursor-pointer min-h-[44px]"
            >
              <option value="">
                {{ $t('Views.OpenSessions.filterSponsorAll') }}
              </option>
              <option
                v-for="s in availableSponsors(sessions)"
                :key="s.id"
                :value="s.id"
              >
                {{ s.name }}
              </option>
            </select>
          </div>

          <!-- Day selector tabs (All tab only) — mirrors the Program /
               live-stream day-tabs row. -->
          <div
            v-if="activeTab === 'all' && availableDays(sessions).length"
            class="os-days"
          >
            <button
              type="button"
              class="os-day"
              :class="{ 'os-day--active': !filters.day }"
              @click="filters.day = ''"
            >
              <span class="os-day__num">
                <i class="far fa-calendar-alt"></i>
              </span>
              <div class="os-day__right">
                <span class="os-day__month">All</span>
                <span class="os-day__name">Days</span>
                <span class="os-day__count"
                  >{{ sessions.length }} sessions</span
                >
              </div>
            </button>
            <button
              v-for="d in availableDays(sessions)"
              :key="d.iso"
              type="button"
              class="os-day"
              :class="{ 'os-day--active': filters.day === d.iso }"
              @click="filters.day = d.iso"
            >
              <span class="os-day__num">{{ d.dayNum }}</span>
              <div class="os-day__right">
                <span class="os-day__month">{{ d.month }}</span>
                <span class="os-day__name">{{ d.dayName }}</span>
                <span class="os-day__count">{{ d.count }} sessions</span>
              </div>
            </button>
          </div>

          <!-- Loading skeletons -->
          <div
            v-if="loading && sessions.length === 0"
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6 sm:gap-y-7"
          >
            <OpenSessionCardSkeleton v-for="i in 6" :key="i" />
          </div>

          <!-- Failed -->
          <div
            v-else-if="failed && sessions.length === 0"
            class="py-16 bg-white rounded-2xl border border-slate-200 text-center text-red-500 px-4 shadow-sm"
          >
            <i
              class="fal fa-exclamation-circle text-2xl text-red-400 block mb-2"
            ></i>
            <p class="text-sm font-semibold mb-3">
              {{ $t('Views.OpenSessions.loadFailed') }}
            </p>
            <button
              type="button"
              class="min-h-[44px] px-4 py-2 rounded-xl text-xs font-bold bg-white border border-slate-200 hover:bg-slate-50"
              @click="refresh"
            >
              {{ $t('Views.OpenSessions.retry') }}
            </button>
          </div>

          <!-- Empty -->
          <div
            v-else-if="visibleSessions(sessions).length === 0"
            class="py-16 bg-white rounded-2xl border border-slate-200 text-center text-slate-500 px-4 shadow-sm"
          >
            <i
              class="fal fa-calendar-day text-2xl text-slate-300 block mb-2"
            ></i>
            <p class="text-sm font-semibold text-slate-700">
              {{ emptyMessage }}
            </p>
          </div>

          <!-- Sessions grid -->
          <div
            v-else
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6 sm:gap-y-7 animate-fade-in"
          >
            <OpenSessionCard
              v-for="s in visibleSessions(sessions)"
              :key="s.id"
              :session="s"
              :pending="isPending(s.id)"
              @open="detailSessionId = $event.id"
              @register="onRegister(register, $event)"
              @unregister="onUnregister(unregister, $event)"
            />
          </div>

          <!-- Detail popup. Bound to the session resolved out of the live list rather than a
               copy taken at open time, so registering from inside the popup updates it in
               place instead of leaving a stale button. -->
          <OpenSessionDetailModal
            v-if="detailSession(sessions)"
            :session="detailSession(sessions)"
            :pending="isPending(detailSessionId)"
            @close="detailSessionId = null"
            @register="onRegister(register, $event)"
            @unregister="onUnregister(unregister, $event)"
          />
        </div>
      </ParticipantDashboardLayout>
    </OpenSessionsDataProvider>
  </CurrentEventDataProvider>
</template>

<script>
import ParticipantDashboardLayout from '@/components/shared/ParticipantDashboardLayout.vue';
import NetworkingQuickNav from '@/components/aiMatching/NetworkingQuickNav.vue';
import SearchPill from '@/components/shared/SearchPill.vue';
import OpenSessionCard from '@/components/openSessions/OpenSessionCard.vue';
import OpenSessionCardSkeleton from '@/components/openSessions/OpenSessionCardSkeleton.vue';
import OpenSessionDetailModal from '@/components/openSessions/OpenSessionDetailModal.vue';
import { formatEventDateTime, eventDateKey } from '@/helpers/event-time';
import { englishOnlyI18nMixin } from '@/mixins';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'mine', label: 'Mine' }
];

export default {
  name: 'OpenSessions',
  components: {
    ParticipantDashboardLayout,
    NetworkingQuickNav,
    SearchPill,
    OpenSessionCard,
    OpenSessionCardSkeleton,
    OpenSessionDetailModal
  },
  mixins: [englishOnlyI18nMixin],
  data() {
    return {
      activeTab: 'all',
      tabs: TABS,
      // Id rather than the object: the popup re-resolves against the live list on every
      // render, so an optimistic register/unregister is reflected without a re-open.
      detailSessionId: null,
      filters: {
        search: '',
        day: '',
        sponsorId: ''
      }
    };
  },
  computed: {
    emptyMessage() {
      if (this.activeTab === 'mine') {
        return this.$t('Views.OpenSessions.emptyMine');
      }
      const hasFilter =
        this.filters.search || this.filters.day || this.filters.sponsorId;
      return hasFilter
        ? this.$t('Views.OpenSessions.emptyFiltered')
        : this.$t('Views.OpenSessions.emptyAll');
    },
    eventTz() {
      return this.$store.getters['CurrentEvent/getTimezone'];
    },
    eventMode() {
      return this.$store.getters['CurrentEvent/getTimezoneMode'];
    }
  },
  methods: {
    // Returns null once the open session leaves the list (e.g. a refresh drops it), which
    // unmounts the popup rather than leaving it showing a session that no longer exists.
    detailSession(sessions) {
      if (!this.detailSessionId) return null;
      return sessions.find((s) => s.id === this.detailSessionId) || null;
    },
    dayKey(iso) {
      return eventDateKey(iso, this.eventTz, this.eventMode);
    },
    availableDays(sessions) {
      const map = new Map();
      sessions.forEach((s) => {
        const key = this.dayKey(s.startAt);
        if (!key) return;
        if (!map.has(key)) {
          map.set(key, {
            iso: key,
            dayNum: formatEventDateTime(
              s.startAt,
              this.eventTz,
              this.eventMode,
              { day: 'numeric' }
            ),
            month: formatEventDateTime(
              s.startAt,
              this.eventTz,
              this.eventMode,
              { month: 'short' }
            ),
            dayName: formatEventDateTime(
              s.startAt,
              this.eventTz,
              this.eventMode,
              { weekday: 'long' }
            ),
            count: 0
          });
        }
        map.get(key).count += 1;
      });
      return Array.from(map.values());
    },
    availableSponsors(sessions) {
      const map = new Map();
      sessions.forEach((s) => {
        if (!s.sponsorId || map.has(s.sponsorId)) return;
        map.set(s.sponsorId, {
          id: s.sponsorId,
          name: s.sponsorName || s.sponsorId
        });
      });
      return Array.from(map.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    },
    visibleSessions(sessions) {
      let list = sessions;
      if (this.activeTab === 'mine') {
        list = list.filter((s) => s.isRegistered);
      } else {
        if (this.filters.day) {
          list = list.filter(
            (s) => this.dayKey(s.startAt) === this.filters.day
          );
        }
        if (this.filters.sponsorId) {
          list = list.filter((s) => s.sponsorId === this.filters.sponsorId);
        }
        const q = this.filters.search.trim().toLowerCase();
        if (q) {
          list = list.filter((s) => {
            const hay = `${s.title || ''} ${s.sponsorName || ''} ${
              s.description || ''
            }`.toLowerCase();
            return hay.includes(q);
          });
        }
      }
      return list;
    },
    async onRegister(registerAction, session) {
      try {
        await registerAction(session.id);
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('Views.OpenSessions.toastRegistered'),
          duration: 3000
        });
      } catch (e) {
        // Optimistic rollback already happened in the store; just toast the failure.
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('Views.OpenSessions.toastRegisterFailed'),
          duration: 4000
        });
      }
    },
    async onUnregister(unregisterAction, session) {
      try {
        await unregisterAction(session.id);
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('Views.OpenSessions.toastUnregistered'),
          duration: 3000
        });
      } catch (e) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('Views.OpenSessions.toastUnregisterFailed'),
          duration: 4000
        });
      }
    }
  }
};
</script>

<style scoped>
/* Day selector tabs — mirrors Program.vue `.prog-days` / `.prog-day`. */
.os-days {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.os-days::-webkit-scrollbar {
  display: none;
}
.os-day {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  background: #ffffff;
  transition: border-color 0.15s;
  flex-shrink: 0;
  min-width: 160px;
  text-align: left;
}
.os-day:hover {
  border-color: var(--color-primary, #047873);
}
.os-day--active {
  border-color: var(--color-primary, #047873);
  border-width: 2px;
}
.os-day__num {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.os-day__num i {
  font-size: 22px;
}
.os-day--active .os-day__num {
  color: var(--color-primary, #047873);
}
.os-day__right {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.os-day__month {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1.2;
}
.os-day__name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  line-height: 1.2;
}
.os-day__count {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.2;
}

@media (max-width: 640px) {
  .os-day {
    padding: 10px 14px;
    min-width: 130px;
    gap: 8px;
  }
  .os-day__num {
    font-size: 22px;
  }
}

.animate-fade-in {
  animation: fadeIn 0.3s ease-out forwards;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

select {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='none'%20stroke='%2364748b'%20stroke-width='2'%20stroke-linecap='round'%20stroke-linejoin='round'%3E%3Cpolyline%20points='6%209%2012%2015%2018%209'%3E%3C/polyline%3E%3C/svg%3E") !important;
  background-repeat: no-repeat !important;
  background-position: right 0.875rem center !important;
  background-size: 1rem !important;
  padding-right: 2.25rem !important;
}
</style>
